import {
    useAppDisplayModeContext,
    useStyles,
    Container,
    Divider,
    useTheme,
} from '@archipro-design/aria';

import { Link } from '@remix-run/react';
import { TileGrid } from '@modules/category/component/tile-grid/TileGrid';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';

import * as S from '@modules/professional/component/shared/PaginatedStyles.style';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import type { ProductTileViewModel } from '@modules/search/util/elasticsearch-product-tile-view';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import type { SyntheticEvent } from 'react';
import { useState } from 'react';
import FilterDialogMobile from '@modules/professional/component/shared/FilterDialog.mobile';
import { scrollToEventElement } from '~/modules/professional/util/filter-sort-helper';
import type { DefaultElasticSearchResults } from '~/modules/professional/type';
import { useTracker } from '@archipro-website/tracker';
import ShoppingAvailable from '~/modules/tile/component/product-tile/ShoppingAvailable';
import VariantDot from '~/modules/tile/component/product-tile/VariantDot';
import { useToggle } from 'ahooks';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import type { GetTileHotButtonsProps } from '~/modules/root/hook';
import {
    useMatchesHandleData,
    useTileHotButtons,
    useAppData,
} from '~/modules/root/hook';
import type { ElasticSearchCategories } from '~/modules/search/type';

import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { Container as PageLayoutContainer } from '@modules/professional/component/page-layout/PageLayout.style';
import ProductTile from '~/modules/tile/component/product-tile/ProductImpressionTile';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import type { FavouriteItem } from '~/modules/design-board/type';
import * as SS from './PagainatedProducts.style';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import MoreProductsComing from '~/modules/products-category/component/DetailedProductSpliter';
import { useProfessionalInitialRouteData } from '../../hook/use-professional-initial-route-data';
import findLastIndex from 'lodash.findlastindex';
import { getExtraVariantCount } from '~/modules/product/util/get-extra-variant-count';
import {
    PRODUCT_TILE_BULLET_SIZE,
    PRODUCT_TILE_COLLAPSED_WIDTH,
    PRODUCT_TILE_EXPANDED_WIDTH,
    PRODUCT_TILE_PAGING_WIDTH,
} from '~/routes/_app.products.$';
import { hasFeature } from '@modules/root';
import AwardIndicator from '@modules/page-builder/component/detail/banner-awards/AwardIndicator';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';

export interface PaginatedProductsProp extends DefaultElasticSearchResults {
    productTiles: ProductTileViewModel[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedProducts: React.FC<PaginatedProductsProp> = ({
    pagination,
    productTiles,
    searchResult,
    filterStates,
    disableTileLink,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const professionalLoaderData = useProfessionalInitialRouteData();
    const professionalTitle = professionalLoaderData?.Title ?? '';
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop } = appDisplayMode;
    const { favouriteItem } = useSaveToDesignBoard('ProductPin');
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const tracker = useTracker();

    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] = useToggle(
        !disableSort
    );
    const gridColumns = desktop ? (filterDrawerOpen ? 4 : 5) : 2;
    const gridColumnWidth = filterDrawerOpen
        ? PRODUCT_TILE_COLLAPSED_WIDTH
        : PRODUCT_TILE_EXPANDED_WIDTH;
    const gridColumnPagingSpacer =
        (gridColumnWidth - PRODUCT_TILE_PAGING_WIDTH) / 2;

    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const productIDs = productTiles.map((product) => product.tile.id);

    const { getTileTopActions, hotButtonModal, getTileAdminCaption } =
        useTileHotButtons({
            itemType: 'product',
            itemIDs: productIDs,
        });

    const renderTileAs = disableTileLink ? 'div' : Link;

    const onTileClick = (product: ProductTileViewModel) => {
        tracker.log('ProfessionalProductClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });

        if ((isBMPreview || disableTileLink) && Boolean(product.tile.canEdit)) {
            visitBMEditor(product.tile, true);
        }
    };

    const tiles = productTiles.map((product) => {
        return (
            <ProductTileMapped
                key={product.tile.id}
                product={product}
                disableTileLink={disableTileLink}
                isBMPreview={isBMPreview}
                getTileTopActions={getTileTopActions}
                professionalTitle={professionalTitle}
                willDelete={willDelete}
                getTileAdminCaption={getTileAdminCaption}
                renderTileAs={renderTileAs}
                desktop={desktop}
                filterDrawerOpen={filterDrawerOpen}
                disableSaveToDesignBoard={disableSaveToDesignBoard}
                favouriteItem={favouriteItem}
                onTileClick={onTileClick}
                gridColumnPagingSpacer={gridColumnPagingSpacer}
                showAwardBadge={enableAwardBadges && product.tile.hasAwards}
            />
        );
    });

    if (filterStates?.isPurchasable?.checked) {
        const lastPurchasbaleItemIndex = findLastIndex(
            productTiles,
            (t) => t.tile.canPurchase
        );
        const purchasableCount =
            productTiles?.filter((t) => t.tile.canPurchase).length ?? 0;
        if (
            purchasableCount === 0 ||
            (lastPurchasbaleItemIndex !== undefined &&
                lastPurchasbaleItemIndex !== -1 &&
                lastPurchasbaleItemIndex !== productTiles.length - 1)
        ) {
            const moreProductsComing = (
                <MoreProductsComing
                    key="MoreProductsComing"
                    professionalTitle={professionalTitle}
                    columns={gridColumns}
                />
            );
            tiles.splice(lastPurchasbaleItemIndex + 1, 0, moreProductsComing);
        }
    }

    const totalCount = searchResult?.nbHits ?? 0;
    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    const navFilterButton = {
        children: `Filters`,
        transparent: true,
        onClick: (event: SyntheticEvent<HTMLElement>) => {
            !desktop && scrollToEventElement(event);
            setTimeout(() => {
                setOpenFilterDialog(true);
            }, 200);
        },
    };

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;
    const category = useMatchesHandleData<ElasticSearchCategories>(
        'category',
        'project'
    );

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <Container>
            {desktop && (
                <div className={css(PageLayoutContainer)}>
                    <Divider
                        size={2}
                        variables={{
                            dividerPadding: 0,
                            dividerPrimaryColor: siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
            <div className={css(S.Wrap)}>
                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <div className={css(S.FilterSection)}>
                                <CategoryFilterNav
                                    category="product"
                                    searchFilters={searchFilters}
                                />
                            </div>
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                    content={
                        <>
                            <div className={css(S.FilterTogglesSection)}>
                                <SearchFilter
                                    searchFilters={searchFilters}
                                    filterButton={navFilterButton}
                                    initialStates={filterStates}
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                    disableSort={disableSort}
                                />
                            </div>
                            {searchFilters.appliedFilters &&
                                searchFilters.appliedFilters.length > 0 && (
                                    <AppliedFilters
                                        filterItems={
                                            searchFilters.appliedFilters
                                        }
                                        resetFilters={() =>
                                            searchFilters.resetFilters(
                                                resetFilterStates(
                                                    searchFilters.filterStates,
                                                    [
                                                        ...ignoreFilterKeys,
                                                        'category',
                                                    ]
                                                )
                                            )
                                        }
                                    />
                                )}
                            <div className={css(S.ResultSection)}>
                                {!tiles.length ? (
                                    <NoSearchResults resultsType={'products'} />
                                ) : (
                                    <TileGrid
                                        items={tiles}
                                        columns={gridColumns}
                                        columnWidth={gridColumnWidth}
                                        pagination={pagination}
                                        paginationItem={'Products'}
                                        onPageChange={(page: number) => {
                                            tracker.log(
                                                'ProfessionalProductPageChange',
                                                {
                                                    url: new URL(
                                                        window.location.href
                                                    ),
                                                    targetTracker:
                                                        'archiproTracker',
                                                    data: {
                                                        ExtraData: page,
                                                    },
                                                }
                                            );
                                        }}
                                        type="products"
                                    />
                                )}
                            </div>
                        </>
                    }
                />
                {!disableSaveToDesignBoard && favouriteItem.target && (
                    <SaveToDesignBoard {...favouriteItem} />
                )}
                {itemIDToDelete && (
                    <DeleteItemConfirmModal
                        title={'Delete product'}
                        content={
                            'Are you sure you would like to delete this product? All information and associated tags will be lost.'
                        }
                        open={true}
                        onCancel={cancelDelete}
                        onConfirm={confirmDelete}
                    />
                )}
                <FilterDialogMobile
                    openFilterDialog={openFilterDialog}
                    itemCount={totalCount}
                    onClose={() => setOpenFilterDialog(false)}
                    showPriceSortBy={true}
                    filterComponent={<div>product-filters goes here</div>}
                />
                {hotButtonModal}
            </div>
        </Container>
    );
};

const ProductTileMapped = ({
    product,
    disableTileLink,
    isBMPreview,
    getTileTopActions,
    willDelete,
    getTileAdminCaption,
    renderTileAs,
    desktop,
    filterDrawerOpen,
    disableSaveToDesignBoard,
    favouriteItem,
    onTileClick,
    professionalTitle,
    gridColumnPagingSpacer,
    showAwardBadge,
}: {
    product: ProductTileViewModel;
    disableTileLink: boolean | undefined;
    isBMPreview: boolean;
    getTileTopActions: <T>({
        item,
        adminItem,
        onDeleteClick,
        editOnSameTab,
    }: GetTileHotButtonsProps<T>) => JSX.Element | undefined;
    willDelete: (itemIDToDelete: number) => void;
    getTileAdminCaption: (
        adminItem: number | DirectoryItemAdmin
    ) => JSX.Element | undefined;
    renderTileAs: 'div' | typeof Link;
    desktop: boolean;
    filterDrawerOpen: boolean;
    disableSaveToDesignBoard: boolean | undefined;
    favouriteItem: FavouriteItem;
    onTileClick: (product: ProductTileViewModel) => void;
    professionalTitle: string;
    gridColumnPagingSpacer: number;
    showAwardBadge?: boolean;
}) => {
    const { css } = useStyles({
        draftOrPending: product.draft || product.pending,
    });
    const {
        productLink,
        canPurchase,
        variants,
        professionalLink: _professionalLink,
        bmEditorLink: _bmEditorLink,
        professionalID: _professionalID,
        image,
        downloads,
        ...rest
    } = product.tile;

    const showSlider = useABTestIsOn('directory-tile-slider');
    const showDownloads = useABTestIsOn('directory-tile-downloads');
    const images = showSlider ? product.sliderImages : image;

    const editOnSameTab = disableTileLink || isBMPreview;

    const topActions = getTileTopActions({
        item: product.tile,
        editOnSameTab,
        onDeleteClick: () => willDelete(product.tile.id),
    });
    const adminCaption = getTileAdminCaption(product.tile.id);

    const linkProps = !disableTileLink
        ? {
              to: productLink,
              prefetch: 'intent',
              target: '_parent',
          }
        : {};

    const styleRules = [SS.ProductTile];

    const topContent = [];

    if (product.draft || product.pending) {
        topContent.push(
            <DraftOrPending
                key="draft-or-pending"
                draft={product.draft}
                pending={product.pending}
            />
        );
    }

    if (canPurchase) {
        topContent.push(
            <ShoppingAvailable inline={true} key="shopping-available" />
        );
    }

    const paddleIndent =
        images.length < 5 ? (5 - images.length) * PRODUCT_TILE_BULLET_SIZE : 0;
    const paginationPadding = gridColumnPagingSpacer + paddleIndent;
    return (
        <ProductTile
            key={productLink}
            as={renderTileAs}
            {...linkProps}
            className={css(...styleRules)}
            {...rest}
            image={images.map((image) => {
                return {
                    src: image.src,
                    mixBlendMode: image.mixBlendMode,
                    sizes: getImagesSizes(348),
                    alt: rest.productName,
                };
            })}
            professionalName={professionalTitle}
            variant={desktop ? '01' : '02'}
            size={desktop && !filterDrawerOpen ? 'large' : 'medium'}
            hideFavourite={disableSaveToDesignBoard}
            onFavourite={(e) => favouriteItem.onClick(e, product.productId)}
            isFavourited={favouriteItem.favourited(product.productId)}
            price={product.tile.price}
            pricePrefix={product.tile.pricePrefix}
            onClick={(e: SyntheticEvent) => {
                if (isBMPreview) {
                    // prevent default to avoid navigating to public page, onTileClick should handle the navigation in BM
                    e.preventDefault();
                }
                onTileClick(product);
            }}
            topContent={topContent}
            variants={variants?.map((v) => ({
                children: (
                    <VariantDot
                        {...v}
                        size={desktop ? 13 : 9}
                        colorCode={v.colorCode ?? ''}
                    />
                ),
            }))}
            impression={{
                type: 'LibraryItem_Directory',
                itemID: product.productId,
                professionalID: product.tile.professionalID,
            }}
            sectionSource={'Profile'}
            additionalVariantCount={getExtraVariantCount(variants?.length)}
            topActions={topActions}
            adminCaption={adminCaption}
            newDesignEnabled={true}
            variables={{
                paginationPadding,
            }}
            awardIndicator={showAwardBadge && <AwardIndicator size={'sm'} />}
            downloads={showDownloads ? downloads : undefined}
        />
    );
};

const DraftOrPending = ({
    draft,
    pending,
}: {
    draft?: boolean;
    pending?: boolean;
}) => {
    const { css } = useStyles();
    return (
        <div className={css(SS.DraftOrPendingBadge)}>
            {draft ? 'DRAFT' : pending ? 'PENDING' : null}
        </div>
    );
};
